import React from "react";

/**
 * Contact support popup is commented out.
 * API failures still surface through the toast; this no longer opens the
 * Connection problem dialog (Support page / Email us).
 */
export default function GlobalApiErrorBoundary() {
  return null;
}

/*
Previous popup — restore by replacing this component with the block below.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  clearApiError,
  subscribeApiError,
} from "../errors/apiErrorBus";
import {
  SUPPORT_SEND_FAILURE_MESSAGE,
  SUPPORT_SEND_SUCCESS_MESSAGE,
  buildErrorDiagnostics,
  buildSupportGmailComposeUrl,
  buildSupportMailto,
  buildSupportOutlookComposeUrl,
  buildSupportPageUrl,
  copySupportEmailDetails,
} from "../errors/supportContacts";
import { sendSupportReport } from "../services/supportReportApi";

export default function GlobalApiErrorBoundary() {
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [showMailBackup, setShowMailBackup] = useState(false);

  useEffect(() => {
    return subscribeApiError({
      onError: (detail) => {
        setSending(false);
        setSendStatus(null);
        setShowMailBackup(false);
        setError(detail);
      },
      onClear: () => {
        setSending(false);
        setSendStatus(null);
        setShowMailBackup(false);
        setError(null);
      },
    });
  }, []);

  const handleClose = useCallback(() => {
    clearApiError();
    setSending(false);
    setSendStatus(null);
    setShowMailBackup(false);
    setError(null);
  }, []);

  const handleRetry = useCallback(() => {
    clearApiError();
    setSending(false);
    setSendStatus(null);
    setShowMailBackup(false);
    setError(null);
    window.location.reload();
  }, []);

  // Try in-app support email first; on failure offer mailto without naming providers.
  const handleEmailUs = useCallback(async () => {
    if (!error || sending) return;
    const diagnostics = buildErrorDiagnostics(error);
    setSending(true);
    setSendStatus(null);
    setShowMailBackup(false);
    try {
      await sendSupportReport(diagnostics);
      setSendStatus({
        severity: "success",
        message: SUPPORT_SEND_SUCCESS_MESSAGE,
      });
    } catch {
      setSendStatus({
        severity: "error",
        message: SUPPORT_SEND_FAILURE_MESSAGE,
      });
      setShowMailBackup(true);
      try {
        await copySupportEmailDetails(diagnostics);
      } catch {
        // Clipboard may be blocked; mailto still offered below.
      }
      window.location.href = buildSupportMailto(diagnostics);
    } finally {
      setSending(false);
    }
  }, [error, sending]);

  if (!error) {
    return null;
  }

  const diagnostics = buildErrorDiagnostics(error);
  const mailtoHref = buildSupportMailto(diagnostics);
  const gmailHref = buildSupportGmailComposeUrl(diagnostics);
  const outlookHref = buildSupportOutlookComposeUrl(diagnostics);
  const supportPath = buildSupportPageUrl(diagnostics);

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="global-api-error-title"
      data-testid="global-api-error-dialog"
    >
      <DialogTitle id="global-api-error-title">
        Connection problem
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="error" variant="outlined">
            {error.message}
          </Alert>
          {error.requestId ? (
            <Typography variant="body2" color="text.secondary">
              Request ID: {error.requestId}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            You can retry, email us, or open the support page for more options.
          </Typography>
          {sendStatus ? (
            <Alert
              severity={sendStatus.severity}
              variant="outlined"
              data-testid="global-api-error-send-status"
            >
              {sendStatus.message}
            </Alert>
          ) : null}
          {showMailBackup ? (
            <Typography variant="body2" color="text.secondary">
              Details were copied when possible. If a mail window did not open,
              use Open mail app or the support page.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button
          component={RouterLink}
          to={supportPath}
          onClick={handleClose}
          data-testid="global-api-error-support-link"
        >
          Support page
        </Button>
        <Button
          onClick={handleEmailUs}
          disabled={sending}
          data-testid="global-api-error-email-button"
        >
          {sending ? "Sending…" : "Email us"}
        </Button>
        {showMailBackup ? (
          <>
            <Button
              component="a"
              href={gmailHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="global-api-error-gmail-backup"
            >
              Open in Gmail
            </Button>
            <Button
              component="a"
              href={outlookHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="global-api-error-outlook-backup"
            >
              Open in Outlook
            </Button>
            <Button
              component="a"
              href={mailtoHref}
              data-testid="global-api-error-mailto-backup"
            >
              Open mail app
            </Button>
          </>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {error.retryable ? (
          <Button
            variant="contained"
            onClick={handleRetry}
            data-testid="global-api-error-retry-button"
          >
            Retry
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
*/
